<!-- jobs-list.component.html -->

<div class="jobs">

  <!-- ── Page Header ─────────────────────────────── -->
  <div class="jobs__header">
    <div>
      <h1 class="jobs__title">Jobs</h1>
      <p class="jobs__subtitle">Manage, assign and track all field jobs.</p>
    </div>
    <a routerLink="/create-job" class="jobs__new-btn">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
        <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
      New Job
    </a>
  </div>

  <!-- ── Status Summary Chips ────────────────────── -->
  <div class="jobs__chips" *ngIf="!loading">
    <button class="jobs__chip"
            [class.jobs__chip--active]="selectedStatus === ''"
            (click)="selectedStatus = ''; applyFilters()">
      <span class="jobs__chip-val">{{ jobs.length }}</span>
      All Jobs
    </button>
    <button class="jobs__chip jobs__chip--pending"
            [class.jobs__chip--active]="selectedStatus === JobStatus.Pending"
            (click)="selectedStatus = JobStatus.Pending; applyFilters()">
      <span class="jobs__chip-dot"></span>
      <span class="jobs__chip-val">{{ countByStatus(JobStatus.Pending) }}</span>
      Pending
    </button>
    <button class="jobs__chip jobs__chip--progress"
            [class.jobs__chip--active]="selectedStatus === JobStatus.InProgress"
            (click)="selectedStatus = JobStatus.InProgress; applyFilters()">
      <span class="jobs__chip-dot"></span>
      <span class="jobs__chip-val">{{ countByStatus(JobStatus.InProgress) }}</span>
      In Progress
    </button>
    <button class="jobs__chip jobs__chip--complete"
            [class.jobs__chip--active]="selectedStatus === JobStatus.Completed"
            (click)="selectedStatus = JobStatus.Completed; applyFilters()">
      <span class="jobs__chip-dot"></span>
      <span class="jobs__chip-val">{{ countByStatus(JobStatus.Completed) }}</span>
      Completed
    </button>
    <button class="jobs__chip jobs__chip--failed"
            [class.jobs__chip--active]="selectedStatus === JobStatus.Failed"
            (click)="selectedStatus = JobStatus.Failed; applyFilters()">
      <span class="jobs__chip-dot"></span>
      <span class="jobs__chip-val">{{ countByStatus(JobStatus.Failed) }}</span>
      Failed
    </button>
  </div>

  <!-- ── Filter Bar ───────────────────────────────── -->
  <div class="jobs__filters">

    <!-- Search -->
    <div class="jobs__search-wrap">
      <svg class="jobs__search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/>
        <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
      <input
        class="jobs__search"
        type="text"
        placeholder="Search by title or location…"
        [(ngModel)]="searchTerm"
        (input)="applyFilters()"
      />
    </div>

    <!-- Worker filter -->
    <div class="jobs__select-wrap">
      <svg class="jobs__select-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
        <path d="M2 20C2 17 5 14 9 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <circle cx="17" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
      </svg>
      <select
        class="jobs__select"
        [(ngModel)]="selectedWorker"
        (change)="applyFilters()">
        <option value="">All Workers</option>
        <option *ngFor="let worker of workers" [ngValue]="worker.id">
          {{ worker.fullName }}
        </option>
      </select>
    </div>

    <!-- Reset -->
    <button class="jobs__reset-btn"
            (click)="resetFilters()"
            [disabled]="!searchTerm && selectedStatus === '' && selectedWorker === ''">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <path d="M3 12C3 7 7.03 3 12 3C15.69 3 18.88 5.06 20.5 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <path d="M21 12C21 17 16.97 21 12 21C8.31 21 5.12 18.94 3.5 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        <polyline points="20.5 3 20.5 8 15.5 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Reset
    </button>

    <span class="jobs__result-count" *ngIf="!loading">
      {{ filteredJobs.length }} job{{ filteredJobs.length !== 1 ? 's' : '' }}
    </span>

  </div>

  <!-- ── Table Card ──────────────────────────────── -->
  <div class="jobs__card">

    <!-- Loading -->
    <div class="jobs__loading" *ngIf="loading">
      <div class="jobs__spinner"></div>
      <span>Loading jobs…</span>
    </div>

    <!-- Empty state -->
    <div class="jobs__empty" *ngIf="!loading && filteredJobs.length === 0">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.4"/>
        <path d="M16 7V5C16 3.9 15.1 3 14 3H10C8.9 3 8 3.9 8 5V7"
              stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
      </svg>
      <p>{{ searchTerm || selectedStatus !== '' || selectedWorker ? 'No jobs match your filters.' : 'No jobs created yet.' }}</p>
      <a routerLink="/create-job" class="jobs__empty-cta" *ngIf="!searchTerm && selectedStatus === '' && !selectedWorker">
        Create your first job
      </a>
    </div>

    <!-- Table -->
    <div class="jobs__table-wrap" *ngIf="!loading && filteredJobs.length > 0">
      <table class="jobs__table">
        <thead>
          <tr>
            <th class="jobs__th">Job</th>
            <th class="jobs__th">Location</th>
            <th class="jobs__th jobs__th--center">Status</th>
            <th class="jobs__th">Assigned Worker</th>
            <th class="jobs__th">Date</th>
            <th class="jobs__th jobs__th--center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr class="jobs__row" *ngFor="let job of pagedJobs">

            <!-- Job title -->
            <td class="jobs__td">
              <div class="jobs__title-cell">
                <div class="jobs__job-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M16 7V5C16 3.9 15.1 3 14 3H10C8.9 3 8 3.9 8 5V7"
                          stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </div>
                <span class="jobs__job-name">{{ job.title }}</span>
              </div>
            </td>

            <!-- Location -->
            <td class="jobs__td">
              <div class="jobs__location">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22C12 22 19 14.2 19 9C19 5.1 15.9 2 12 2Z"
                        stroke="currentColor" stroke-width="1.8"/>
                  <circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.8"/>
                </svg>
                {{ job.locationName }}
              </div>
            </td>

            <!-- Status -->
            <td class="jobs__td jobs__td--center">
              <span class="jobs__status-badge"
                    [class.jobs__status-badge--pending]="job.status === JobStatus.Pending"
                    [class.jobs__status-badge--progress]="job.status === JobStatus.InProgress"
                    [class.jobs__status-badge--complete]="job.status === JobStatus.Completed"
                    [class.jobs__status-badge--failed]="job.status === JobStatus.Failed">
                <span class="jobs__status-dot"></span>
                {{ getStatusLabel(job.status) }}
              </span>
            </td>

            <!-- Assigned Worker -->
            <td class="jobs__td">
              <div class="jobs__assign-cell">
                <div class="jobs__assign-avatar"
                     *ngIf="job.assignedUserId"
                     [title]="getWorkerName(job.assignedUserId)">
                  {{ getInitials(getWorkerName(job.assignedUserId)) }}
                </div>
                <select class="jobs__assign-select"
                        [(ngModel)]="job.assignedUserId"
                        (change)="assignWorker(job)">
                  <option [ngValue]="null">Unassigned</option>
                  <option *ngFor="let worker of workers" [ngValue]="worker.id">
                    {{ worker.fullName }}
                  </option>
                </select>
              </div>
            </td>

            <!-- Date -->
            <td class="jobs__td jobs__td--muted">
              {{ job.assignedDate | date:'MMM d, y' }}
            </td>

            <!-- Actions -->
            <td class="jobs__td jobs__td--center">
              <div class="jobs__actions">

                <!-- Status quick-update dropdown -->
                <div class="jobs__action-group">
                  <button class="jobs__action-btn jobs__action-btn--progress"
                          (click)="updateStatus(job, JobStatus.InProgress)"
                          [disabled]="job.status === JobStatus.InProgress"
                          title="Mark In Progress">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
                      <polyline points="12 6 12 12 16 14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    </svg>
                  </button>

                  <button class="jobs__action-btn jobs__action-btn--complete"
                          (click)="updateStatus(job, JobStatus.Completed)"
                          [disabled]="job.status === JobStatus.Completed"
                          title="Mark Complete">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>

                <!-- Detail / Logs links -->
                <a [routerLink]="['/jobs', job.id]"
                   class="jobs__action-btn jobs__action-btn--view"
                   title="View Details">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
                          stroke="currentColor" stroke-width="1.8"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                </a>

                <a [routerLink]="['/jobs', job.id, 'logs']"
                   class="jobs__action-btn jobs__action-btn--logs"
                   title="View Logs">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                          stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </a>

                <!-- Delete -->
                <button class="jobs__action-btn jobs__action-btn--delete"
                        (click)="deleteJob(job.id)"
                        title="Delete Job">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <polyline points="3 6 5 6 21 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <path d="M19 6L18.1 20.1C18 21.1 17.1 22 16.1 22H7.9C6.9 22 6 21.1 5.9 20.1L5 6"
                          stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <path d="M10 11V17M14 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6"
                          stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                </button>

              </div>
            </td>

          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="jobs__pagination" *ngIf="totalPages > 1">
        <span class="jobs__pagination-info">
          Showing {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, filteredJobs.length) }}
          of {{ filteredJobs.length }}
        </span>
        <div class="jobs__pagination-controls">
          <button class="jobs__page-btn"
                  (click)="goToPage(currentPage - 1)"
                  [disabled]="currentPage === 1">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="jobs__page-btn jobs__page-btn--num"
                  *ngFor="let p of pageNumbers"
                  [class.jobs__page-btn--active]="p === currentPage"
                  (click)="goToPage(p)">
            {{ p }}
          </button>
          <button class="jobs__page-btn"
                  (click)="goToPage(currentPage + 1)"
                  [disabled]="currentPage === totalPages">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

    </div>
  </div>

  <!-- ── Delete Confirm Modal ────────────────────── -->
  <div class="jobs__modal-overlay" *ngIf="confirmDeleteId" (click)="confirmDeleteId = null">
    <div class="jobs__modal" (click)="$event.stopPropagation()">
      <div class="jobs__modal-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
          <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="currentColor" stroke-width="1.2"/>
        </svg>
      </div>
      <h3 class="jobs__modal-title">Delete Job?</h3>
      <p class="jobs__modal-desc">This action cannot be undone. All associated logs will remain.</p>
      <div class="jobs__modal-actions">
        <button class="jobs__modal-cancel" (click)="confirmDeleteId = null">Cancel</button>
        <button class="jobs__modal-confirm" (click)="confirmDelete()">Delete</button>
      </div>
    </div>
  </div>

</div>